/**
 * The one vocabulary for where a workspace is. Board lanes, List chips, the Overview bar,
 * the dashboard and the document page all use these names, built from the operational state
 * (what actually happened: quotes, invoices, orders, bookings), plus Completed / Cancelled.
 */
import {
  OPERATIONAL_QUEUES,
  OPERATIONAL_STATES,
  type OperationalQueueId,
  type OperationalStateId,
} from './operationalState';

export type WorkspaceStage = OperationalQueueId | 'completed' | 'cancelled';

/** Workspace tab where the work for each stage happens (URL ids from types.ts). */
export type WorkspaceActionTab = 'quotes' | 'invoices' | 'supplier' | 'implementation' | 'aftercare';

export const WORKSPACE_STAGE_LIST: Array<{ id: WorkspaceStage; label: string; tab: WorkspaceActionTab | null }> = [
  { id: 'presale', label: 'Pre-sale', tab: 'quotes' },
  { id: 'deposit', label: 'Deposit', tab: 'invoices' },
  { id: 'ordering', label: 'Ordering', tab: 'supplier' },
  { id: 'installation', label: 'Installation', tab: 'implementation' },
  { id: 'aftercare', label: 'Aftercare', tab: 'aftercare' },
  { id: 'completed', label: 'Completed', tab: null },
  { id: 'cancelled', label: 'Cancelled', tab: null },
];

/** Tab labels a "next step" points at, matching the workspace tab names. */
export const ACTION_TAB_LABELS: Record<WorkspaceActionTab, string> = {
  quotes: 'Quotes',
  invoices: 'Invoices',
  supplier: 'Ordering',
  implementation: 'Installation',
  aftercare: 'Aftercare',
};

export function workspaceStageLabel(stage: WorkspaceStage): string {
  return WORKSPACE_STAGE_LIST.find((entry) => entry.id === stage)?.label || 'Pre-sale';
}

export function workspaceStageTab(stage: WorkspaceStage): WorkspaceActionTab | null {
  return WORKSPACE_STAGE_LIST.find((entry) => entry.id === stage)?.tab ?? null;
}

export function isWorkspaceStage(value: string | null | undefined): value is WorkspaceStage {
  return WORKSPACE_STAGE_LIST.some((entry) => entry.id === value);
}

/** Stage for a lifecycle status and (for active workspaces) operational state. */
export function stageFor(
  lifecycle: 'Active' | 'Completed' | 'Cancelled',
  state?: OperationalStateId | null,
): WorkspaceStage {
  if (lifecycle === 'Completed') return 'completed';
  if (lifecycle === 'Cancelled') return 'cancelled';
  return state ? OPERATIONAL_STATES[state].queue : 'presale';
}

/** Short state wording shown under the stage, e.g. "Deposit requested". */
export function stateShortLabel(state: OperationalStateId | null | undefined): string {
  return state ? OPERATIONAL_STATES[state].short : '';
}

export function statesInStage(stage: WorkspaceStage): OperationalStateId[] {
  return OPERATIONAL_QUEUES.find((queue) => queue.id === stage)?.states || [];
}
